import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { Users, MapPin, Wrench, ClipboardList, ListOrdered, LogOut, BarChart, ChevronDown, ChevronRight, Database, User, Zap, Calendar as CalendarIcon } from 'lucide-react';
import KundenVerwaltung from './pages/KundenVerwaltung';
import StandortVerwaltung from './pages/StandortVerwaltung';
import MitarbeiterVerwaltung from './pages/MitarbeiterVerwaltung';
import AuftragsListe from './pages/AuftragsListe';
import AuftragsUebersicht from './pages/AuftragsBearbeitung';
import AuftragsDetails from './pages/AuftragsDetails';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import MonteurDashboard from './pages/MonteurDashboard';
import Calendar from './pages/Calendar';
import { AuthProvider, useAuth } from './lib/AuthContext';
import NotificationBell from './components/NotificationBell';

// ScrollToTop component to handle scroll position on navigation
function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return null;
}

function ProtectedRoute({ children, allowedRoles = ['admin', 'monteur'] }: { 
  children: React.ReactNode, 
  allowedRoles?: Array<'admin' | 'monteur'> 
}) {
  const { user, loading, userRole } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Laden...</span>
      </div>
    );
  }

  if (!user) {
    // Save the attempted URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!allowedRoles.includes(userRole || 'admin')) {
    return <Navigate to={userRole === 'monteur' ? '/monteur' : '/'} replace />;
  }

  return <>{children}</>;
}

function UserProfileDropdown() {
  const { signOut, user, userRole, userName } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
  };

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center h-16 px-4 text-gray-700 hover:text-blue-600 gap-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <User size={20} />
        <span className="hidden md:inline">{userName || user?.email || 'Benutzer'}</span>
        <ChevronDown 
          size={16} 
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 py-1 border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">{userName || user?.email}</p>
              <p className="text-xs text-gray-500">
                {userRole === 'admin' ? 'Administrator' : 'Monteur'}
              </p>
            </div>
            
            <button
              onClick={handleSignOut}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 gap-2"
            >
              <LogOut size={16} className="text-red-500" />
              <span>Abmelden</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Navigation() {
  const { user, userRole } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  if (userRole === 'monteur') {
    return (
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-1">
              <Link to="/" className="flex items-center h-16 px-4">
                <img
                  src="/updated_logo.jpg"
                  alt="Elektro Skoeries Logo"
                  className="h-12 w-auto"
                />
              </Link>
              <Link
                to="/monteur"
                className={`flex items-center h-16 px-4 gap-2 transition-colors ${
                  isActive('/monteur') 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                <Zap size={20} />
                <span className="hidden md:inline">Meine Aufträge</span>
              </Link>
              <Link
                to="/kalender"
                className={`flex items-center h-16 px-4 gap-2 transition-colors ${
                  isActive('/kalender') 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                <CalendarIcon size={20} />
                <span className="hidden md:inline">Kalender</span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <UserProfileDropdown />
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center space-x-1">
            <Link to="/" className="flex items-center h-16 px-4">
              <img
                src="/updated_logo.jpg"
                alt="Elektro Skoeries Logo"
                className="h-12 w-auto"
              />
            </Link>
            <Link
              to="/dashboard"
              className={`flex items-center h-16 px-4 gap-2 transition-colors ${
                isActive('/dashboard') 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <BarChart size={20} />
              <span className="hidden md:inline">Übersicht</span>
            </Link>
            
            <div className="relative group h-16 flex items-center">
              <button
                className={`flex items-center h-16 px-4 gap-2 focus:outline-none transition-colors ${
                  isActive('/bearbeitung') || isActive('/auftraege') || isActive('/meine-auftraege')
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                <ClipboardList size={20} />
                <span className="hidden md:inline">Aufträge</span>
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </button>
              
              <div className="absolute left-0 top-16 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top scale-95 group-hover:scale-100">
                <Link
                  to="/bearbeitung"
                  className={`flex items-center px-4 py-3 text-sm hover:bg-gray-100 gap-2 ${
                    isActive('/bearbeitung') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  <ClipboardList size={16} />
                  <span>Auftragsübersicht</span>
                </Link>
                <Link
                  to="/auftraege"
                  className={`flex items-center px-4 py-3 text-sm hover:bg-gray-100 gap-2 ${
                    isActive('/auftraege') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  <ListOrdered size={16} />
                  <span>Auftragserfassung</span>
                </Link>
                <Link
                  to="/meine-auftraege"
                  className={`flex items-center px-4 py-3 text-sm hover:bg-gray-100 gap-2 border-t border-gray-100 ${
                    isActive('/meine-auftraege') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  <Zap size={16} />
                  <span>Meine Aufträge</span>
                </Link>
              </div>
            </div>
            
            <div className="relative group h-16 flex items-center">
              <button
                className={`flex items-center h-16 px-4 gap-2 focus:outline-none transition-colors ${
                  isActive('/kunden') || isActive('/mitarbeiter')
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                <Database size={20} />
                <span className="hidden md:inline">Stammdaten</span>
                <ChevronDown size={16} className="transition-transform group-hover:rotate-180" />
              </button>
              
              <div className="absolute left-0 top-16 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top scale-95 group-hover:scale-100">
                <Link
                  to="/kunden"
                  className={`flex items-center px-4 py-3 text-sm hover:bg-gray-100 gap-2 rounded-t-md ${
                    isActive('/kunden') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  <Users size={16} />
                  <span>Kunden & Standorte</span>
                </Link>
                <Link
                  to="/mitarbeiter"
                  className={`flex items-center px-4 py-3 text-sm hover:bg-gray-100 gap-2 rounded-b-md ${
                    isActive('/mitarbeiter') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  <Wrench size={16} />
                  <span>Mitarbeiter</span>
                </Link>
              </div>
            </div>

            <Link
              to="/kalender"
              className={`flex items-center h-16 px-4 gap-2 transition-colors ${
                isActive('/kalender') 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              <CalendarIcon size={20} />
              <span className="hidden md:inline">Kalender</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <UserProfileDropdown />
          </div>
        </div>
      </div>
    </nav>
  );
}

function AppRoutes() {
  const { userRole } = useAuth();

  return (
    <>
      <Navigation />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route
            path="/monteur"
            element={
              <ProtectedRoute allowedRoles={['monteur']}>
                <MonteurDashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/auftraege"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AuftragsListe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bearbeitung"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AuftragsUebersicht />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bearbeitung/:id"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AuftragsDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meine-auftraege"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MonteurDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kunden"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <KundenVerwaltung />
              </ProtectedRoute>
            }
          />
          <Route
            path="/standorte"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <StandortVerwaltung />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mitarbeiter"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MitarbeiterVerwaltung />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kalender"
            element={
              <ProtectedRoute allowedRoles={['admin', 'monteur']}>
                <Calendar />
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/" 
            element={
              <Navigate to={userRole === 'monteur' ? '/monteur' : '/dashboard'} replace />
            } 
          />
          
          <Route 
            path="*" 
            element={
              <Navigate to={userRole === 'monteur' ? '/monteur' : '/dashboard'} replace />
            } 
          />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;